import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { SITE_ORIGIN, BUSINESS_ID } from "./LocalBusinessJsonLd";

const crumbLinkClass =
  "text-slate-500 transition-colors hover:text-teal-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600/40 focus-visible:ring-offset-2 rounded-sm";

/**
 * Home > Services > {label} — visible breadcrumb nav plus the matching
 * BreadcrumbList and Service JSON-LD for a single commercial landing page.
 * Only rendered on Tier-1 repair pages and Tier-2 /services/* detail pages;
 * never on Home, About, Contact, Blogs, the Services hub, or NotFound.
 */
export default function PageBreadcrumb({
  label,
  path,
  description,
  serviceType,
}) {
  const url = `${SITE_ORIGIN}${path}`;

  const crumbs = [
    { name: "Home", item: `${SITE_ORIGIN}/` },
    { name: "Services", item: `${SITE_ORIGIN}/services` },
    { name: label, item: url },
  ];

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.item,
    })),
  };

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: label,
    description,
    url,
    serviceType,
    provider: { "@id": BUSINESS_ID },
    areaServed: {
      "@type": "City",
      name: "Temecula",
    },
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
      </Helmet>

      <nav aria-label="Breadcrumb" className="mb-4 text-xs sm:text-sm">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li className="flex items-center gap-1.5">
            <Link to="/" className={crumbLinkClass}>
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-400" aria-hidden />
          </li>
          <li className="flex items-center gap-1.5">
            <Link to="/services" className={crumbLinkClass}>
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-400" aria-hidden />
          </li>
          <li aria-current="page" className="font-medium text-slate-600">
            {label}
          </li>
        </ol>
      </nav>
    </>
  );
}
